import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const DepartmentForm = ({ currentUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const isSimple = currentUser?.isSimple || false;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    operation_id: '',
    unit_id: '',
    is_active: true,
  });

  const [operations, setOperations] = useState([]);
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);

  const loadInitialData = useCallback(async () => {
    try {
      setLoadingData(true);
      const dashboardResponse = await axios.get('/dashboard');
      if (dashboardResponse.data.success) {
        setOperations(dashboardResponse.data.data.operations || []);
        setUnits(dashboardResponse.data.data.units || []);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados iniciais' });
    } finally {
      setLoadingData(false);
    }
  }, []);

  const loadDepartment = useCallback(async () => {
    try {
      setLoadingData(true);
      const response = await axios.get(`/departments/${id}`);
      if (response.data.success) {
        const dept = response.data.data;
        setFormData({
          name: dept.name || '',
          description: dept.description || '',
          operation_id: dept.operation?.id || '',
          unit_id: dept.unit?.id || '',
          is_active: dept.is_active !== false,
        });
      }
    } catch (error) {
      console.error('Error loading department:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar departamento' });
    } finally {
      setLoadingData(false);
    }
  }, [id]);

  useEffect(() => {
    if (isSimple) {
      navigate('/departments', { replace: true });
      return;
    }
    loadInitialData();
    if (isEdit) loadDepartment();
  }, [isEdit, isSimple, navigate, loadInitialData, loadDepartment]);

  useEffect(() => {
    if (formData.operation_id) setFilteredUnits(units);
    else setFilteredUnits([]);
  }, [formData.operation_id, units]);

  if (isSimple) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setMessage(null);

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description || '',
        operation_id: parseInt(formData.operation_id),
        unit_id: parseInt(formData.unit_id),
        is_active: formData.is_active,
      };

      if (isEdit) {
        await axios.put(`/departments/${id}`, payload);
        setMessage({ type: 'success', text: 'Departamento atualizado com sucesso!' });
      } else {
        await axios.post('/departments', payload);
        setMessage({ type: 'success', text: 'Departamento criado com sucesso!' });
      }

      setTimeout(() => navigate('/departments'), 1200);
    } catch (error) {
      console.error('Department error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erro ao salvar departamento',
      });
      if (error.response?.data?.errors) setErrors(error.response.data.errors);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="department-form-page">
        <div className="df-container">
          <div className="df-title-card">
            <h1>Carregando…</h1>
            <p>Buscando operações, unidades e dados do departamento.</p>
          </div>

          <div className="df-form-card">
            <div className="df-loading">
              <div className="spinner-border" role="status" aria-label="Carregando"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="department-form-page">
      <div className="df-container">
        {/* Title */}
        <div className="df-title-card">
          <h1>{isEdit ? 'Editar Departamento' : 'Criar Novo Departamento'}</h1>
          <p>Preencha os dados abaixo para {isEdit ? 'atualizar' : 'criar'} o departamento.</p>
        </div>

        {/* Alerts */}
        {message && (
          <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
            <div className="df-alert-row">
              <span>{message.text}</span>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setMessage(null)} />
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="df-form-card">
            <div className="df-content">
              {/* Grid 2x2 */}
              <div className="df-grid">
                {/* Operação e Unidade */}
                <section className="df-card">
                  <header className="df-card-head">
                    <div className="df-card-title">
                      <i className="fas fa-building" />
                      <span>Operação e Unidade</span>
                    </div>
                    <span className="df-card-badge">{isEdit ? 'Bloqueado' : 'Obrigatório'}</span>
                  </header>

                  <div className="df-card-body">
                    <div className="df-row-2">
                      <div className="df-field">
                        <label htmlFor="operation_id" className="form-label">
                          Operação <span className="text-danger">*</span>
                        </label>
                        <select
                          id="operation_id"
                          name="operation_id"
                          className={`form-select ${errors.operation_id ? 'is-invalid' : ''}`}
                          value={formData.operation_id}
                          onChange={handleChange}
                          required
                          disabled={isEdit}
                        >
                          <option value="">Selecione uma operação</option>
                          {operations.map((op) => (
                            <option key={op.id} value={op.id}>
                              {op.name}
                            </option>
                          ))}
                        </select>
                        {errors.operation_id && <div className="invalid-feedback d-block">{errors.operation_id}</div>}
                      </div>

                      <div className="df-field">
                        <label htmlFor="unit_id" className="form-label">
                          Unidade <span className="text-danger">*</span>
                        </label>
                        <select
                          id="unit_id"
                          name="unit_id"
                          className={`form-select ${errors.unit_id ? 'is-invalid' : ''}`}
                          value={formData.unit_id}
                          onChange={handleChange}
                          required
                          disabled={isEdit}
                        >
                          <option value="">Selecione uma unidade</option>
                          {filteredUnits.map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.name}
                            </option>
                          ))}
                        </select>
                        {errors.unit_id && <div className="invalid-feedback d-block">{errors.unit_id}</div>}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Nome */}
                <section className="df-card">
                  <header className="df-card-head">
                    <div className="df-card-title">
                      <i className="fas fa-heading" />
                      <span>Nome</span>
                    </div>
                    <span className="df-card-badge">Obrigatório</span>
                  </header>

                  <div className="df-card-body">
                    <div className="df-field">
                      <label htmlFor="name" className="form-label">
                        Nome <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Digite o nome do departamento"
                      />
                      {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                    </div>
                  </div>
                </section>

                {/* Descrição */}
                <section className="df-card">
                  <header className="df-card-head">
                    <div className="df-card-title">
                      <i className="fas fa-align-left" />
                      <span>Descrição</span>
                    </div>
                    <span className="df-card-badge df-card-badge-muted">Opcional</span>
                  </header>

                  <div className="df-card-body">
                    <div className="df-field">
                      <label htmlFor="description" className="form-label">
                        Descrição
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                        value={formData.description}
                        onChange={handleChange}
                        rows={5}
                        placeholder="Descreva o departamento (opcional)"
                      />
                      {errors.description && <div className="invalid-feedback d-block">{errors.description}</div>}
                    </div>
                  </div>
                </section>

                {/* Status */}
                <section className="df-card">
                  <header className="df-card-head">
                    <div className="df-card-title">
                      <i className="fas fa-toggle-on" />
                      <span>Status</span>
                    </div>
                    <span className="df-card-badge df-card-badge-muted">Controle</span>
                  </header>

                  <div className="df-card-body">
                    <label className="df-check">
                      <input
                        type="checkbox"
                        id="is_active"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                      />
                      <span className="df-check-box" aria-hidden="true" />
                      <span className="df-check-text">
                        <strong>Departamento Ativo</strong>
                        <small>Marque se o departamento está ativo</small>
                      </span>
                    </label>

                    {errors.is_active && <div className="invalid-feedback d-block">{errors.is_active}</div>}
                  </div>
                </section>
              </div>
            </div>

            {/* Actions */}
            <div className="df-actions">
              <Link to="/departments" className="btn btn-secondary df-btn">
                <i className="fas fa-times" /> Cancelar
              </Link>

              <button type="submit" className="btn btn-primary df-btn df-btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save" /> {isEdit ? 'Salvar Alterações' : 'Criar Departamento'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DepartmentForm;
